import DropUploadIcon from '@src/svgs/DropUploadIcon';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import type { FileRejection } from 'react-dropzone';

/** Story videos the pipeline accepts (mediaValidation ACCEPTED_VIDEO_MIME_TYPES). */
const STORY_VIDEO_ACCEPT = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov', '.qt'],
};

export const UNSUPPORTED_VIDEO_MESSAGE =
  'Only MP4 and MOV videos can be uploaded';
export const ONE_VIDEO_ONLY_MESSAGE = 'Drop one video at a time';

type Props = {
  onFiles: (files: File[]) => void;
  /** Tells the person why a dropped file was refused. */
  showToast: (toast: { state: 'error'; message: string }) => void;
  /** Accept several videos at once (the Multiple Videos mode). */
  multiple?: boolean;
  /** The formats and limit, on one line ("MP4 or MOV · up to 100 GB"). */
  hint: string;
  /** The `<input>` test hook, kept stable for the e2e suite. */
  inputTestId?: string;
  className?: string;
};

/**
 * The drop box for story videos (docs/design-system.md → Drop Zones → Drop
 * box): drop or browse, the formats line, and a toast that says why a file was
 * refused. One component for Single and Multiple Videos, so the two modes of
 * Add Stories cannot drift apart again.
 */
export default function VideoDropBox({
  onFiles,
  showToast,
  multiple = false,
  hint,
  inputTestId,
  className,
}: Props) {
  const onDropRejected = (rejections: FileRejection[]) => {
    const tooMany = rejections.some(r =>
      r.errors.some(e => e.code === 'too-many-files'),
    );
    showToast({
      state: 'error',
      message: tooMany ? ONE_VIDEO_ONLY_MESSAGE : UNSUPPORTED_VIDEO_MESSAGE,
    });
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: STORY_VIDEO_ACCEPT,
    multiple,
    onDropAccepted: onFiles,
    onDropRejected,
  });

  return (
    <Box
      {...getRootProps()}
      className={className}
      $active={isDragActive}
      data-testid="story-video-dropzone"
    >
      <input {...getInputProps()} data-testid={inputTestId} />
      <DropUploadIcon size="sm" />
      <Title>
        {multiple ? 'Drop your videos here' : 'Drop your video here'} or{' '}
        <Browse>browse</Browse>
      </Title>
      <Hint>{hint}</Hint>
    </Box>
  );
}

const Box = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  box-sizing: border-box;
  padding: var(--spacing-lg) var(--spacing-md);
  text-align: center;
  border: 1px dashed
    ${props => (props.$active ? 'var(--primary)' : 'var(--border)')};
  border-radius: var(--radius-button);
  background: ${props =>
    props.$active ? 'var(--surface-elevated)' : 'var(--surface-subtle)'};
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    background-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
    background: var(--surface-elevated);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const Title = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
`;

const Browse = styled.span`
  color: var(--primary);
`;

const Hint = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-muted);
`;
